import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";

// DNC-459: reached from the "add"/"change" link on the Sales Representatives row of classic
// accounts.adp. Same shape as SetTermDate, which the "Set Last Date of Term" button on that page
// already opens, so support sees a layout they know. Styled like AccountsIsland (card + label
// column) because that is the other account-scoped SPA page reached from accounts.adp.

// callApi throws an Error carrying the HTTP status, and only a 404 actually means the account is
// missing. Reporting a 500 or a dropped connection as "not found" sends whoever is triaging down
// the wrong path. (401 never reaches a caller — callApi redirects to /login.)
function accountLoadErrorText(err: unknown, acctId: string): string {
  return (err as { status?: number })?.status === 404
    ? `Account "${acctId}" not found.`
    : `Could not load account "${acctId}". Please try again.`;
}

type SalesRep = {
  id: number;
  name: string;
};

// What GetSalesRep returns. callApi is typed Promise<any>, so without this the whole response —
// including the res.current narrowing below — is unchecked.
type GetSalesRepResponse = {
  acctId: string;
  name: string;
  current: SalesRep | null;
  reps: SalesRep[];
};

export default function SetSalesRep() {
  const [searchParams] = useSearchParams();
  // `acctId` is only the search box. The account actually loaded is `loadedAcctId`, and that is
  // what a save writes to — otherwise typing a new code without pressing Load would leave the
  // card showing the old account while Save wrote the rep to the newly typed one.
  const [acctId, setAcctId] = useState(searchParams.get("acctId") || "");
  const [loadedAcctId, setLoadedAcctId] = useState("");
  const [accountName, setAccountName] = useState("");
  const [currentRep, setCurrentRep] = useState<SalesRep | null>(null);
  const [reps, setReps] = useState<SalesRep[]>([]);
  const [selectedId, setSelectedId] = useState("");
  // Which action is in flight, so the spinner lands on the button that started it. "" is idle;
  // either value disables the whole form.
  const [busy, setBusy] = useState<"" | "load" | "save">("");
  const loading = busy !== "";
  // `type` goes straight into an `alert-${type}` class, so keep it to the two Bootstrap
  // variants this page actually uses rather than any string.
  const [message, setMessage] = useState<{
    type: "danger" | "success";
    text: string;
  } | null>(null);

  useEffect(() => {
    if (searchParams.get("acctId")) {
      loadAccount(searchParams.get("acctId")!);
    }
    // Mount only: the link from accounts.adp is a full page load, so the query string cannot
    // change under us. Same as SetTermDate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadAccount(id: string) {
    // The same two conditions the Load button is disabled on, enforced here rather than at each
    // call site: Enter in the account box reaches this directly, and there is nothing to cancel an
    // in-flight request with, so a second load can resolve after the first and leave the card
    // showing one account's rep list under another account's name — or overwrite what a save just
    // wrote. Guarding the function covers the keyboard, the button and the mount effect at once.
    if (loading || !id) return;

    setMessage(null);
    setBusy("load");
    try {
      const res: GetSalesRepResponse = await callApi(
        `sysadmin/accounts/GetSalesRep?acctId=${encodeURIComponent(id)}`,
        HttpMethod.GET,
      );
      const loadedReps: SalesRep[] = res.reps ?? [];
      const current = res.current ?? null;
      setAccountName(res.name);
      setAcctId(res.acctId);
      setLoadedAcctId(res.acctId);
      setCurrentRep(current);
      setReps(loadedReps);
      // Only preselect a rep the dropdown actually offers. An account's current rep is often a
      // departed rep (ContactInfo.type 'formersalesteam') that the dropdown will not list.
      // Preselecting their id would leave the <select> blank while the Save button stayed enabled,
      // and Save would then silently no-op because the id matches nothing in `reps`. Blank instead
      // forces a real pick. (Referral agents used to reach here too; the API now filters them out.)
      setSelectedId(
        current != null && loadedReps.some((r) => r.id === current.id)
          ? String(current.id)
          : "",
      );
    } catch (err) {
      setMessage({ type: "danger", text: accountLoadErrorText(err, id) });
      setLoadedAcctId("");
      setAccountName("");
      setCurrentRep(null);
      setReps([]);
      setSelectedId("");
    } finally {
      setBusy("");
    }
  }

  async function save() {
    const rep = reps.find((r) => String(r.id) === selectedId);
    // Same reentrancy guard loadAccount carries. Nothing reaches this while busy today — the Save
    // button is the only way in and it is disabled — but keeping the two entry points symmetrical
    // means adding an Enter-to-save shortcut later cannot reintroduce the race.
    if (loading || !rep || !loadedAcctId) return;

    setMessage(null);
    setBusy("save");
    try {
      const res = await callApi(
        "sysadmin/accounts/SetSalesRep",
        HttpMethod.POST,
        { acctId: loadedAcctId, salesPersonId: rep.id },
      );
      setMessage({ type: "success", text: res.message });
      setCurrentRep(rep);
    } catch {
      setMessage({ type: "danger", text: "Failed to update the sales rep." });
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="p-3">
      {message && (
        <div
          className={`alert alert-${message.type} alert-dismissible`}
          role="alert"
        >
          {message.text}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setMessage(null)}
          />
        </div>
      )}

      {/* "Account Code" rather than "Account ID" — that is what the search box on
          accounts.adp calls it, and support arrives from there. */}
      <div className="row mb-3 align-items-end">
        <div className="col-auto">
          <label className="form-label fw-bold" htmlFor="acctIdInput">
            Account Code
          </label>
          <input
            id="acctIdInput"
            className="form-control"
            value={acctId}
            onChange={(e) => setAcctId(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && loadAccount(acctId)}
            // "either value disables the whole form" — this input included, so the box stops
            // looking interactive mid-request. loadAccount guards the race itself.
            disabled={loading}
            placeholder="e.g. DEMO"
          />
        </div>
        <div className="col-auto">
          <button
            className="btn btn-primary"
            onClick={() => loadAccount(acctId)}
            disabled={!acctId || loading}
          >
            {busy === "load" && (
              <span
                className="spinner-border spinner-border-sm me-2"
                aria-hidden="true"
              />
            )}
            {busy === "load" ? "Loading…" : "Load"}
          </button>
        </div>
      </div>

      {/* Gated on loadedAcctId, not the search box: everything below describes the account that
          was actually loaded, and a save writes to that one. */}
      {loadedAcctId && (
        <div className="card" style={{ maxWidth: 720 }}>
          <div className="card-header">Account Information</div>
          <div className="card-body">
            <div className="row mb-3">
              <label className="col-md-3 fw-bold">AcctId</label>
              <div className="col-md-9">{loadedAcctId}</div>
            </div>
            <div className="row mb-3">
              <label className="col-md-3 fw-bold">Name</label>
              <div className="col-md-9">{accountName}</div>
            </div>
            <div className="row mb-3">
              <label className="col-md-3 fw-bold">Current Sales Rep</label>
              <div className="col-md-9">
                {currentRep ? (
                  currentRep.name
                ) : (
                  <span className="text-muted">None</span>
                )}
              </div>
            </div>
            <div className="row align-items-center">
              <label className="col-md-3 fw-bold" htmlFor="salesRepSelect">
                New Sales Rep
              </label>
              <div className="col-md-9">
                <select
                  id="salesRepSelect"
                  className="form-select"
                  value={selectedId}
                  disabled={loading}
                  onChange={(e) => setSelectedId(e.target.value)}
                >
                  <option value="">Select a sales rep</option>
                  {reps.map((rep) => (
                    <option key={rep.id} value={rep.id}>
                      {rep.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          {/* Note left, action right — the standard card footer pattern. The note is worth saying
              out loud, and worth being exact about: commission reports resolve the rep live, so a
              save is not purely forward-looking. Don't soften this back to "future invoices only". */}
          <div className="card-footer d-flex justify-content-between align-items-center gap-3">
            <small className="text-muted">
              Saving replaces the sales rep on this account. Referral agents are
              left alone. Commission statements and the invoice summary resolve
              the rep at the time they are generated, so re-running a report for
              a month that has already closed will credit the new rep.
            </small>
            <button
              className="btn btn-primary flex-shrink-0"
              onClick={save}
              disabled={!selectedId || loading}
            >
              {busy === "save" && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                />
              )}
              {busy === "save" ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
